/**
 * RouteTable — Gateway > Route Table.
 *
 * The gateway's ACTIVE forwarding state, read live over SSH each time you ask for it. A uCentral
 * gateway does not publish its FIB in its state document (re-measured on all three live gateways
 * 2026-09-20), so there is no passive feed to subscribe to and nothing to poll. That shapes the
 * whole screen: fetch once, show WHEN it was read, and make Refresh an explicit act.
 *
 * The interesting structure on these boxes is POLICY routing. Each downstream VLAN gets its own
 * table with a rule like `from all iif down5v1050 lookup 4`, so a plain flat list of routes would
 * hide the thing an operator actually needs to see. Tables are therefore the top-level unit, each
 * labelled by the interface its rule steers, with Main first.
 *
 * Read-only by construction — the backend only ever sends `ip route show` / `ip rule show`.
 * Editing static routes is a different screen and still blocked: the uCentral schema has no
 * static-route object (Gateway > Routing says exactly that).
 */
import { useSession } from "@/stores/session";
import { useGatewayRoutes } from "@/lib/api/hooks";
import { PageHeader, ErrorBanner, EmptyState } from "@/components/ui/Page";
import { Button } from "@/components/ui/Button";
import type { GatewayRoute, GatewayRoutesEntry, GatewayRouteTable } from "@/types/domain";

/** "2 minutes ago" — the read is a point-in-time sample, so its age is part of the data. */
function ago(iso?: string): string {
  if (!iso) return "";
  const secs = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (secs < 60) return `${secs}s ago`;
  if (secs < 3600) return `${Math.round(secs / 60)}m ago`;
  return `${Math.round(secs / 3600)}h ago`;
}

export default function RouteTable() {
  const siteId = useSession((s) => s.currentSiteId);
  const { data, isLoading, isFetching, error, refetch } = useGatewayRoutes(siteId);

  if (!siteId) {
    return (
      <div>
        <PageHeader title="Route Table" subtitle="Active routes on the site's gateways" />
        <EmptyState
          title="Select a site"
          hint="Routes are read from a site's gateways. Pick a site in the scope bar."
        />
      </div>
    );
  }

  const gateways = data?.gateways ?? [];

  return (
    <div>
      <PageHeader
        title="Route Table"
        subtitle="How each gateway decides where traffic goes — read live from the device"
        actions={
          <Button variant="secondary" onClick={() => refetch()} disabled={isFetching}>
            {isFetching ? "Reading…" : "Refresh"}
          </Button>
        }
      />

      {error && <ErrorBanner error={error} />}

      {isLoading && (
        <div className="text-sm text-ink-muted">
          Opening a session to each gateway — this takes a moment.
        </div>
      )}

      {data && gateways.length === 0 && (
        <EmptyState
          title="No gateways at this site"
          hint="Route tables are read from gateways. Adopt one and it will appear here."
        />
      )}

      <div className="flex flex-col gap-4">
        {gateways.map((gw) => (
          <GatewayCard key={gw.serial} gw={gw} />
        ))}
      </div>
    </div>
  );
}

function GatewayCard({ gw }: { gw: GatewayRoutesEntry }) {
  return (
    <section className="md-card p-4">
      <header className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
        <div className="flex items-baseline gap-2">
          <h2 className="text-base font-semibold text-ink">{gw.name}</h2>
          {gw.role && (
            <span className="rounded bg-surface-2 px-1.5 py-0.5 text-[0.625rem] uppercase tracking-wide text-ink-subtle">
              {gw.role}
            </span>
          )}
          <span className="font-mono text-xs text-ink-subtle">{gw.serial}</span>
        </div>
        {gw.fetched_at && (
          <span className="text-xs text-ink-subtle" title={gw.fetched_at}>
            read {ago(gw.fetched_at)}
            {gw.address ? ` from ${gw.address}` : ""}
          </span>
        )}
      </header>

      {gw.error ? (
        /* A gateway we could not read states WHY inline, rather than vanishing from the page —
           the credential case in particular tells the operator exactly where to store it. */
        <div className="rounded border border-line bg-surface-2 p-3">
          <div className="text-sm font-medium text-ink">Could not read this gateway</div>
          <p className="mt-1 text-sm text-ink-muted">{gw.error.message}</p>
        </div>
      ) : gw.tables.length === 0 ? (
        <p className="text-sm text-ink-muted">The gateway returned no routes.</p>
      ) : (
        <div className="flex flex-col gap-4">
          {gw.tables.map((t) => (
            <TableBlock key={t.id} table={t} />
          ))}
        </div>
      )}
    </section>
  );
}

function TableBlock({ table }: { table: GatewayRouteTable }) {
  return (
    <div>
      <div className="mb-1.5 flex flex-wrap items-baseline gap-2">
        <h3 className="text-sm font-medium text-ink">{table.label}</h3>
        {table.rule && (
          /* WHY this table exists. Without the steering rule a numbered table is just a number. */
          <span className="font-mono text-xs text-ink-subtle">{table.rule}</span>
        )}
      </div>
      {table.routes.length === 0 ? (
        /* An empty policy table is a real, load-bearing finding, not a blank: the rule sends
           this interface's traffic here, finds nothing, and falls through to Main. Verified on
           JH-GW-02 2026-09-20, where every per-VLAN table is empty. Say so in words. */
        <p className="rounded border border-line bg-surface-2 px-3 py-2 text-xs text-ink-muted">
          No routes in this table — traffic from{" "}
          <span className="font-mono text-ink">{table.interface ?? "this interface"}</span> falls
          through to <span className="font-medium text-ink">Main</span>.
        </p>
      ) : (
      <div className="overflow-x-auto">
        <table className="w-full text-left text-xs">
          <thead className="text-ink-subtle">
            <tr>
              <th className="py-1 pr-4 font-normal">Destination</th>
              <th className="py-1 pr-4 font-normal">Via</th>
              <th className="py-1 pr-4 font-normal">Interface</th>
              <th className="py-1 pr-4 font-normal">Source</th>
              <th className="py-1 pr-4 font-normal">Proto</th>
              <th className="py-1 pr-4 text-right font-normal">Metric</th>
            </tr>
          </thead>
          <tbody className="font-mono">
            {table.routes.map((r, i) => (
              <RouteRow key={`${r.destination}-${r.interface ?? ""}-${i}`} route={r} />
            ))}
          </tbody>
        </table>
      </div>
      )}
    </div>
  );
}

function RouteRow({ route }: { route: GatewayRoute }) {
  const isDefault = route.destination === "default";
  return (
    /* `title` carries the raw `ip route` line so nothing the parser did not model is lost. */
    <tr className="border-t border-line/60" title={route.raw}>
      <td className={`py-1 pr-4 ${isDefault ? "font-semibold text-ink" : "text-ink"}`}>
        {route.destination}
      </td>
      <td className="py-1 pr-4 text-ink-muted">{route.gateway ?? "—"}</td>
      <td className="py-1 pr-4 text-ink">{route.interface ?? "—"}</td>
      <td className="py-1 pr-4 text-ink-muted">{route.source ?? "—"}</td>
      <td className="py-1 pr-4 text-ink-subtle">{route.proto ?? "—"}</td>
      <td className="py-1 pr-4 text-right tabular-nums text-ink-muted">{route.metric ?? "—"}</td>
    </tr>
  );
}
